import md5 from "js-md5";
import simulator from "@/services/simulator.js";
import gmsh from "@/services/gmsh";
import ngsolve from "@/services/ngsolve";
import { adminRun, clusterField } from "@/common/adminRun";
import { authHeaders } from "@/common/role";
import { build2dPayload, build3dInputs } from "@/store/module-simulator/mutations";

// Comparativo de latência (administradores): a mesma simulação, com a mesma entrada, rodada em
// cada configuração (e repetida N vezes), uma depois da outra. No fim, as saídas de cada execução
// são comparadas numericamente com as da primeira: o resultado físico não pode depender de em
// quantas máquinas a simulação rodou, só o tempo. As execuções não viram produções da simulação
// nem entram no histórico do perfil; ficam no painel de administração (/admin/cluster).

const seconds = value => (Number.isFinite(value) ? value : null);
const MEEP_BASE = process.env.VUE_APP_SIMULATOR_LOCATION || "/meep";
const NGSOLVE_BASE = process.env.VUE_APP_NGSOLVE_LOCATION || "/ngsolve";

function row(config, wallS, run, error, outputs) {
  run = run || {};
  const queueS = run.startedAt && run.submittedAt ? run.startedAt - run.submittedAt : null;
  return {
    label: config.label,
    rep: config.rep || 1,
    requested: config.nodes ? config.nodes.join(", ") : "Slurm decides",
    jobId: run.jobId || null,
    nodes: run.nodes || null,
    numNodes: run.numNodes || null,
    ranks: run.ranks || null,
    threads: run.threads || null,
    plan: run.plan ? run.plan.complexity : null,
    status: error ? "failed" : "completed",
    error: error || "",
    wallS,
    queueS: seconds(queueS),
    solverS: seconds(run.solverS),
    jobS: seconds(run.elapsedS),
    outputs: outputs || {},
    // Preenchidos ao comparar com a primeira execução concluída
    fieldDiff: null,
    spectraDiff: null
  };
}

function detail(err) {
  const data = err.response && err.response.data;
  if (data && typeof data.detail === "string") return data.detail;
  if (data && typeof data.data === "string") return data.data;
  return err.message;
}

// Saídas do Meep: caminhos "./storage/users/..." servidos pela API em /meep/storage/...
function meepOutputs(data) {
  let parsed = {};
  try {
    parsed = JSON.parse(data);
  } catch (err) {
    return {};
  }
  const url = path => (typeof path === "string" && path.startsWith("./storage/") ? `${MEEP_BASE}${path.slice(1)}` : null);
  return {
    field: url(parsed.wave_scene),
    dielectric: url(parsed.scene),
    video: url(parsed.videoName),
    spectra: (parsed.fluxes || []).map(url).filter(Boolean)
  };
}

async function run2d(state, config) {
  const payload = build2dPayload(state);
  const cluster = clusterField(config.nodes ? config : null);
  if (cluster) payload.cluster = cluster;
  const started = performance.now();
  try {
    const { data } = await simulator.post("/exec", payload, { headers: cluster ? await authHeaders() : {} });
    const wallS = (performance.now() - started) / 1000;
    if (data.error) return row(config, wallS, data.run, String(data.data).slice(0, 300));
    return row(config, wallS, data.run, "", meepOutputs(data.data));
  } catch (err) {
    return row(config, (performance.now() - started) / 1000, null, detail(err));
  }
}

async function run3d(state, config, mesh, inputs) {
  const cluster = clusterField(config.nodes ? config : null);
  const production = md5.hex(`${Date.now()}-${config.label}-${config.rep}`);
  const request = {
    title: state.title,
    author: state.author,
    simulation_id: state.id,
    production,
    gmsh_mesh_path: `${state.id}.msh`,
    materials: mesh.materials.map(({ name, epsilon }) => ({ name, eps_r: epsilon })),
    sources: inputs.sources,
    quality: inputs.quality,
    ...(cluster ? { cluster } : {})
  };
  const started = performance.now();
  try {
    const { data } = await ngsolve.post("/simulate", request, { headers: cluster ? await authHeaders() : {} });
    const base = `${NGSOLVE_BASE}/simulate/${state.id}/${production}`;
    return row(config, (performance.now() - started) / 1000, data.run, "", {
      field: `${base}/2d-data`, view3d: `${base}/3d-data`, info: `${base}/info`, csv: `${base}/csv`, vtk: `${base}/vtk`,
      resultsPage: `/datails3d/${state.id}/${production}/`
    });
  } catch (err) {
    return row(config, (performance.now() - started) / 1000, null, detail(err));
  }
}

// ---------------------------------------------------------------- comparação
const getJson = async url => (await fetch(url, { cache: "no-store" })).json();
const flat = value => (Array.isArray(value) ? value.flat(Infinity) : []).map(Number);

// max |a - b| / max |a| (campo); 0 = idêntico
function relativeDiff(a, b) {
  if (!a.length || a.length !== b.length) return null;
  let peak = 0;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    if (!Number.isFinite(a[i]) || !Number.isFinite(b[i])) continue;
    peak = Math.max(peak, Math.abs(a[i]));
    diff = Math.max(diff, Math.abs(a[i] - b[i]));
  }
  return peak ? diff / peak : diff;
}

// max |a - b| entre as curvas de todos os espectros (T, R e perda, que já estão entre 0 e 1)
async function spectraDiff(first, other) {
  if (!first.length || first.length !== other.length) return null;
  let diff = 0;
  for (let i = 0; i < first.length; i++) {
    const [a, b] = await Promise.all([getJson(first[i]), getJson(other[i])]);
    const ya = (a.rows || []).flatMap(r => r.y || []);
    const yb = (b.rows || []).flatMap(r => r.y || []);
    if (ya.length !== yb.length) return null;
    ya.forEach((v, k) => {
      if (Number.isFinite(v) && Number.isFinite(yb[k])) diff = Math.max(diff, Math.abs(v - yb[k]));
    });
  }
  return diff;
}

async function fieldOf(row, is3d) {
  if (!row.outputs.field) return [];
  const data = await getJson(row.outputs.field);
  return flat(is3d ? data.magnitude : data);
}

export async function compareOutputs(results, is3d) {
  const done = results.filter(r => r.status === "completed" && r.outputs.field);
  if (done.length < 2) return;
  const reference = await fieldOf(done[0], is3d);
  done[0].fieldDiff = 0;
  if (!is3d) done[0].spectraDiff = done[0].outputs.spectra.length ? 0 : null;
  for (const result of done.slice(1)) {
    try {
      result.fieldDiff = relativeDiff(reference, await fieldOf(result, is3d));
      if (!is3d) result.spectraDiff = await spectraDiff(done[0].outputs.spectra, result.outputs.spectra);
    } catch (err) {
      result.fieldDiff = null;
    }
  }
}

// ------------------------------------------------------------------- execução
export async function runBenchmark(state, configs, is3d) {
  const bench = adminRun.benchmark;
  Object.assign(bench, { open: true, running: true, kind: is3d ? "3d" : "2d", title: state.title || "",
    results: [], current: 0, error: "", comparing: false, startedAt: new Date().toISOString() });
  try {
    let mesh = null;
    let inputs = null;
    if (is3d) {
      inputs = build3dInputs(state);
      if (!inputs.geometryList.length || !inputs.sources.length) {
        throw new Error("Add at least one geometry and one source before running the comparison.");
      }
      // Mesma malha para todas as configurações: só o NGSolve é comparado
      ({ data: mesh } = await gmsh.post("/", { id: state.id, geometries: inputs.geometryList,
        sources: inputs.sources, quality: inputs.quality }));
    }
    for (let index = 0; index < configs.length; index++) {
      bench.current = index;
      const result = is3d ? await run3d(state, configs[index], mesh, inputs) : await run2d(state, configs[index]);
      bench.results.push(result);
    }
    bench.comparing = true;
    await compareOutputs(bench.results, is3d);
  } catch (err) {
    bench.error = detail(err);
  } finally {
    bench.running = false;
    bench.comparing = false;
    bench.current = -1;
  }
}

// CSV para anexar a um artigo: uma linha por execução
export function benchmarkCsv(bench) {
  const origin = window.location.origin;
  const header = ["simulation", "kind", "configuration", "repetition", "requested_machines", "machines_used", "processes",
    "threads", "queue_s", "solver_s", "job_s", "click_to_result_s", "field_max_rel_diff_vs_first",
    "spectra_max_abs_diff_vs_first", "status", "error", "job_id", "outputs"];
  const quote = value => {
    const text = value == null ? "" : String(value);
    return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const number = (value, digits) => (Number.isFinite(value) ? value.toFixed(digits) : "");
  const lines = bench.results.map(r => {
    const outputs = Object.values(r.outputs || {}).flat().filter(Boolean).map(url => (url.startsWith("/") ? origin + url : url));
    return [bench.title, bench.kind, r.label, r.rep, r.requested, r.nodes, r.ranks, r.threads, number(r.queueS, 3),
      number(r.solverS, 3), number(r.jobS, 3), number(r.wallS, 3),
      Number.isFinite(r.fieldDiff) ? r.fieldDiff.toExponential(3) : "",
      Number.isFinite(r.spectraDiff) ? r.spectraDiff.toExponential(3) : "",
      r.status, r.error, r.jobId, outputs.join(" ")].map(quote).join(",");
  });
  return [header.join(","), ...lines].join("\n");
}
